import { useCallback, useEffect, useState } from "react";
import { invoicesApi, ApiError } from "@/api";
import type { Invoice, InvoicesSummary } from "@/types";
import { Button, Spinner, Pill } from "@/components/ui";
import { invoiceStatusTone, isDeadInvoiceStatus, humanise } from "@/lib/jobs";
import { formatMoney } from "@/lib/money";
import { formatDateShort } from "@/lib/format";
import { CreateInvoiceDrawer } from "./CreateInvoiceDrawer";

const EMPTY_SUMMARY: InvoicesSummary = { count: 0, total_billed: 0, total_paid: 0, total_outstanding: 0 };

function isOverdue(inv: Invoice): boolean {
  if (inv.status === "paid" || isDeadInvoiceStatus(inv.status)) return false;
  const due = new Date(inv.due_date).getTime();
  return Number.isFinite(due) && due < Date.now();
}

/** Invoices tab (Brief 05) — self-contained against GET /invoices?job_id (not in
 *  /detail). Create raises a LIVE Xero invoice and also mutates fees, so after a
 *  create we refresh this list AND signal the fees to reload (onInvoiced). The UI
 *  never writes invoice state — status/payments arrive via the Make/Xero sync. */
export function InvoicesTab({
  jobId,
  hasClient,
  leadClientName,
  defaultReference,
  onNotice,
  onInvoiceCount,
}: {
  jobId: number;
  hasClient: boolean;
  leadClientName: string | null;
  defaultReference: string;
  onNotice: (kind: "info" | "warn", message: string) => void;
  onInvoiceCount?: (count: number) => void;
}) {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<InvoicesSummary>(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(
    (opts: { initial?: boolean } = {}) => {
      let cancelled = false;
      if (opts.initial) setLoading(true);
      invoicesApi
        .listInvoices(jobId)
        .then((res) => {
          if (cancelled) return;
          setInvoices(res.items ?? []);
          setSummary(res.summary ?? EMPTY_SUMMARY);
          onInvoiceCount?.(res.summary?.count ?? 0);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load invoices.");
        })
        .finally(() => {
          if (!cancelled && opts.initial) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    },
    [jobId, onInvoiceCount],
  );

  useEffect(() => load({ initial: true }), [load]);

  function toggle(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (loading) {
    return <div className="jobs-list__loading"><Spinner label="Loading invoices…" /></div>;
  }

  return (
    <div className="job-invoices">
      <div className="job-invoices__head">
        <p className="job-drawer__muted">{summary.count} invoice{summary.count === 1 ? "" : "s"}</p>
        <Button
          variant="primary"
          size="sm"
          disabled={!hasClient}
          title={hasClient ? undefined : "Add a client before invoicing"}
          onClick={() => setShowCreate(true)}
        >
          New invoice
        </Button>
      </div>

      {!hasClient ? (
        <p className="job-drawer__muted">This job has no client — add one in the Clients tab before raising an invoice.</p>
      ) : null}

      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <dl className="job-fees__summary">
        <SummaryStat label="Billed" value={summary.total_billed} />
        <SummaryStat label="Paid" value={summary.total_paid} />
        <SummaryStat label="Outstanding" value={summary.total_outstanding} />
      </dl>

      {invoices.length === 0 ? (
        <p className="jobs-list__empty">No invoices raised on this job yet.</p>
      ) : (
        <ul className="job-invoice-list">
          {invoices.map((inv) => {
            const dead = isDeadInvoiceStatus(inv.status);
            const overdue = isOverdue(inv);
            const open = expanded.has(inv.id);
            return (
              <li key={inv.id} className={`job-invoice${dead ? " job-invoice--muted" : ""}`}>
                <div className="job-invoice__top">
                  <div className="job-invoice__id">
                    <span className="job-invoice__number numeric">{inv.xero_invoice_number || `Invoice #${inv.id}`}</span>
                    <Pill tone={invoiceStatusTone(inv.status)}>{humanise(inv.status)}</Pill>
                    {overdue ? <span className="job-chip job-chip--overdue">Overdue</span> : null}
                  </div>
                  <div className="job-invoice__amounts">
                    <span className="job-invoice__total numeric">{formatMoney(inv.total)}</span>
                    <span className="job-drawer__muted">
                      {formatMoney(inv.amount_paid)} paid · {formatMoney(inv.amount_outstanding)} outstanding
                    </span>
                  </div>
                </div>

                <div className="job-invoice__meta">
                  <span className="job-drawer__muted">
                    Issued {formatDateShort(inv.issue_date)} · Due {formatDateShort(inv.due_date)}
                    {inv.paid_date ? ` · Paid ${formatDateShort(inv.paid_date)}` : ""}
                  </span>
                </div>

                <div className="job-invoice__actions">
                  {inv.xero_url ? (
                    <a className="job-invoice__link" href={inv.xero_url} target="_blank" rel="noreferrer">View in Xero ↗</a>
                  ) : null}
                  {inv.online_invoice_url ? <CopyLinkButton url={inv.online_invoice_url} /> : null}
                  {inv.lines.length > 0 ? (
                    <button type="button" className="job-invoice__toggle" onClick={() => toggle(inv.id)}>
                      {open ? "Hide" : "Show"} {inv.lines.length} line{inv.lines.length === 1 ? "" : "s"}
                    </button>
                  ) : null}
                </div>

                {open ? (
                  <ul className="job-invoice__lines">
                    {inv.lines.map((line) => (
                      <li key={line.id}>
                        <span>{line.description}</span>
                        <span className="job-drawer__muted">{humanise(line.fee_type)}</span>
                        <span className="numeric">{formatMoney(line.amount)}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {showCreate ? (
        <CreateInvoiceDrawer
          jobId={jobId}
          leadClientName={leadClientName}
          defaultReference={defaultReference}
          onClose={() => setShowCreate(false)}
          onSaved={(res) => {
            setShowCreate(false);
            onNotice("info", `Invoice ${res.xero_invoice_number} raised — awaiting payment.`);
            load();
          }}
        />
      ) : null}
    </div>
  );
}

/** Copies the client-facing payment link to the clipboard (more useful than
 *  opening it). Falls back to a prompt if the Clipboard API is unavailable. */
function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy the payment link:", url);
    }
  }
  return (
    <button type="button" className="job-invoice__link job-invoice__copy" onClick={copy} title="Copy payment link to clipboard">
      {copied ? "Copied ✓" : "Copy payment link ⧉"}
    </button>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="job-fees__stat">
      <dt>{label}</dt>
      <dd className="numeric">{formatMoney(value)}</dd>
    </div>
  );
}
